import {
  FACE_HORIZONTAL_EXPAND,
  FACE_UPWARD_BIAS,
  FACE_VERTICAL_EXPAND,
  FALLBACK_UPPER_FRACTION,
  MAX_SAFETY_UPPER_FRACTION,
} from "../config/constants.ts";
import type { Rect } from "../utils/types.ts";
import { clamp } from "../utils/math.ts";

/**
 * Expand a detector face box so the mask covers forehead, hairline, jaw and
 * surrounding head. Expansion is intentionally stronger than a tight crop.
 */
export function expandFaceBox(
  box: Rect,
  frameWidth: number,
  frameHeight: number,
  horizontalExpand = FACE_HORIZONTAL_EXPAND,
  verticalExpand = FACE_VERTICAL_EXPAND,
): Rect {
  const width = box.width * (1 + horizontalExpand);
  const height = box.height * (1 + verticalExpand);
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2 - box.height * FACE_UPWARD_BIAS;
  return clampRect(
    {
      x: cx - width / 2,
      y: cy - height / 2,
      width,
      height,
    },
    frameWidth,
    frameHeight,
  );
}

export function clampRect(rect: Rect, frameWidth: number, frameHeight: number): Rect {
  const x = clamp(rect.x, 0, frameWidth);
  const y = clamp(rect.y, 0, frameHeight);
  const right = clamp(rect.x + rect.width, 0, frameWidth);
  const bottom = clamp(rect.y + rect.height, 0, frameHeight);
  return {
    x,
    y,
    width: Math.max(0, right - x),
    height: Math.max(0, bottom - y),
  };
}

export function mirrorRect(rect: Rect, frameWidth: number): Rect {
  return {
    x: frameWidth - rect.x - rect.width,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  };
}

export function upperRegion(frameWidth: number, frameHeight: number, fraction: number): Rect {
  return {
    x: 0,
    y: 0,
    width: frameWidth,
    height: Math.max(1, frameHeight * fraction),
  };
}

export function fallbackUpperRegion(frameWidth: number, frameHeight: number): Rect {
  return upperRegion(frameWidth, frameHeight, FALLBACK_UPPER_FRACTION);
}

export function maximumSafetyRegion(frameWidth: number, frameHeight: number): Rect {
  return upperRegion(frameWidth, frameHeight, MAX_SAFETY_UPPER_FRACTION);
}

export function isDetectionStale(lastGoodTimestamp: number, now: number, staleMs: number): boolean {
  if (lastGoodTimestamp <= 0) return true;
  return now - lastGoodTimestamp > staleMs;
}

export function shouldActivateFallback(input: {
  hadFace: boolean;
  hasReliableFace: boolean;
  isStale: boolean;
  detectorError: boolean;
}): boolean {
  if (input.detectorError) return true;
  if (!input.hadFace) return true;
  if (!input.hasReliableFace) return true;
  return input.isStale;
}

export function canRecordFromPrivacy(state: {
  cameraActive: boolean;
  concealmentApplied: boolean;
  ready: boolean;
  unsafe: boolean;
}): { ok: boolean; reason?: string } {
  if (!state.cameraActive) {
    return { ok: true };
  }
  if (state.unsafe || !state.ready || !state.concealmentApplied) {
    return {
      ok: false,
      reason:
        "Recording is blocked because privacy processing is not confirmed. Raw camera output cannot be captured.",
    };
  }
  return { ok: true };
}
